import { IExpressionEvaluator } from "../../Models/IExpressionEvaluator";
import { MaybePromise } from "../../Models/MaybePromise";
import { IExpressionEvaluatorContext } from "../../Models/IExpressionEvaluatorContext";
import { ObjectExpression } from "../../Models/ObjectExpression";
import { shallowArrayEqual } from "@@common";

type CacheEntry = { keys: string[]; values: any[]; result: Record<string, any> };

export class ObjectExpressionEvaluator implements IExpressionEvaluator<ObjectExpression> {
  get type(): string {
    return "Object";
  }

  // Define expression properties for variable collection
  // Properties is a Record<string, IExpression> which will be handled specially by collectReferencedVariablesInternal
  expressionProperties = ["Properties"];

  // Option B: cache the last result per (expression, context) pair.
  // Keyed on both because RepeatContainer evaluates the same ObjectExpression once per
  // item, each time with a different child context — a single-key cache would return
  // the last item's result for every item.
  // Two-level WeakMap: expression → context → CacheEntry.
  // Both levels use WeakMap so entries are GC'd automatically when either key is released.
  private cache = new WeakMap<ObjectExpression, WeakMap<IExpressionEvaluatorContext, CacheEntry>>();

  private buildAndCache(
    expression: ObjectExpression,
    context: IExpressionEvaluatorContext,
    keys: string[],
    values: any[],
  ): Record<string, any> {
    let byContext = this.cache.get(expression);
    if (!byContext) {
      byContext = new WeakMap();
      this.cache.set(expression, byContext);
    }

    const cached = byContext.get(context);
    if (cached && shallowArrayEqual(cached.keys, keys) && shallowArrayEqual(cached.values, values)) {
      return cached.result;
    }
    const result: Record<string, any> = {};
    for (let i = 0; i < keys.length; i++) {
      result[keys[i]] = values[i];
    }
    byContext.set(context, { keys, values, result });
    return result;
  }

  evaluate(expression: ObjectExpression, context: IExpressionEvaluatorContext): MaybePromise {
    const keys = Object.keys(expression.Properties);
    const results: any[] = [];
    let hasPromise = false;

    for (const key of keys) {
      const propExpression = expression.Properties[key];
      const value = context.Evaluator.evaluate(propExpression, context);
      results.push(value);
      if (value instanceof Promise) {
        hasPromise = true;
      }
    }

    // If any promise exists, wait for all then check cache before building
    if (hasPromise) {
      return Promise.all(results).then((values) => this.buildAndCache(expression, context, keys, values));
    }

    // All sync — check cache before building
    return this.buildAndCache(expression, context, keys, results);
  }
}



